import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { isAdmin as fetchIsAdmin } from "@/utils/roles.functions";
import { Inbox, MailOpen, ShieldAlert, Trash2, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin-messages")({
  head: () => ({ meta: [{ title: "Messages — Admin — LendyTrack" }, { name: "robots", content: "noindex" }] }),
  component: AdminMessages,
});

type ContactMessage = {
  id: string;
  name: string;
  email: string;
  subject: string | null;
  message: string;
  status: "new" | "in_progress" | "resolved" | "spam";
  created_at: string;
};

function AdminMessages() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [access, setAccess] = useState<"checking" | "allowed" | "denied">("checking");
  const qc = useQueryClient();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate({ to: "/auth" }); return; }
    fetchIsAdmin()
      .then((r) => setAccess(r.isAdmin ? "allowed" : "denied"))
      .catch(() => setAccess("denied"));
  }, [user, authLoading, navigate]);

  const q = useQuery({
    queryKey: ["contact_messages"],
    enabled: access === "allowed",
    queryFn: async (): Promise<ContactMessage[]> => {
      const { data, error } = await supabase
        .from("contact_messages")
        .select("id,name,email,subject,message,status,created_at")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as ContactMessage[];
    },
  });

  // Realtime subscription: new messages light up instantly.
  useEffect(() => {
    if (access !== "allowed") return;
    const channel = supabase
      .channel("contact_messages_admin")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "contact_messages" },
        (payload) => {
          const row = payload.new as ContactMessage;
          toast.success(`New message from ${row.name}`, { description: row.subject ?? row.message.slice(0, 80) });
          qc.invalidateQueries({ queryKey: ["contact_messages"] });
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("New contact message", { body: `${row.name}: ${row.subject ?? row.message.slice(0, 80)}` });
          }
        },
      )
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "contact_messages" }, () => {
        qc.invalidateQueries({ queryKey: ["contact_messages"] });
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "contact_messages" }, () => {
        qc.invalidateQueries({ queryKey: ["contact_messages"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [access, qc]);

  useEffect(() => {
    if (access === "allowed" && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, [access]);

  async function setStatus(id: string, status: ContactMessage["status"]) {
    const { error } = await supabase.from("contact_messages").update({ status }).eq("id", id);
    if (error) toast.error(error.message);
  }
  async function remove(id: string) {
    if (!confirm("Delete this message?")) return;
    const { error } = await supabase.from("contact_messages").delete().eq("id", id);
    if (error) toast.error(error.message);
  }

  if (access !== "allowed") {
    return (
      <AppShell>
        <div className="mx-auto mt-20 max-w-md rounded-2xl border border-border bg-card p-10 text-center shadow-soft">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-danger/10 text-danger">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <h1 className="mt-4 font-display text-xl font-semibold">
            {access === "checking" ? "Verifying access…" : "Admins only"}
          </h1>
          {access === "denied" && (
            <Link to="/dashboard" className="mt-6 inline-block rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
              Back to dashboard
            </Link>
          )}
        </div>
      </AppShell>
    );
  }

  const messages = q.data ?? [];
  const newCount = messages.filter((m) => m.status === "new").length;

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin</div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Contact inbox</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Live updates — new submissions appear automatically.
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card px-4 py-3 text-right shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">New</div>
          <div className="font-display text-2xl font-semibold">{newCount}</div>
        </div>
      </div>

      {q.isLoading ? (
        <div className="rounded-xl border border-border bg-card p-12 text-center text-sm text-muted-foreground">Loading…</div>
      ) : messages.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
          <Inbox className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No messages yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map((m) => (
            <article key={m.id} className={`rounded-xl border bg-card p-5 shadow-soft ${m.status === "new" ? "border-primary/40" : "border-border"}`}>
              <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-display text-base font-semibold">{m.name}</h2>
                    <StatusPill status={m.status} />
                  </div>
                  <a href={`mailto:${m.email}`} className="text-sm text-muted-foreground hover:underline">{m.email}</a>
                  {m.subject && <div className="mt-1 text-sm font-medium">{m.subject}</div>}
                </div>
                <div className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString()}</div>
              </header>
              <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">{m.message}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject ?? "Your message to LendyTrack")}`}
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90">
                  <MailOpen className="h-3 w-3" /> Reply
                </a>
                <button onClick={() => setStatus(m.id, "in_progress")}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted">
                  <Clock className="h-3 w-3" /> In progress
                </button>
                <button onClick={() => setStatus(m.id, "resolved")}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted">
                  <CheckCircle2 className="h-3 w-3" /> Resolve
                </button>
                <button onClick={() => setStatus(m.id, "spam")}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted">
                  <AlertCircle className="h-3 w-3" /> Spam
                </button>
                <button onClick={() => remove(m.id)}
                  className="ml-auto inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-danger hover:bg-danger/10">
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </AppShell>
  );
}

function StatusPill({ status }: { status: ContactMessage["status"] }) {
  const map: Record<ContactMessage["status"], { label: string; cls: string }> = {
    new: { label: "New", cls: "bg-primary/10 text-primary border-primary/30" },
    in_progress: { label: "In progress", cls: "bg-warning/10 text-warning border-warning/30" },
    resolved: { label: "Resolved", cls: "bg-success/10 text-success border-success/30" },
    spam: { label: "Spam", cls: "bg-danger/10 text-danger border-danger/30" },
  };
  const m = map[status];
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${m.cls}`}>{m.label}</span>;
}
